import React from 'react';
import { RestoreOperation } from '../../types/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface RestoreHistoryCardProps {
  restore: RestoreOperation;
  deploymentId: string;
  onDelete?: (restoreId: string) => void;
}

const RestoreHistoryCard: React.FC<RestoreHistoryCardProps> = ({ restore, deploymentId, onDelete }) => {
  const { t } = (useTranslation as any)('backups');
  const navigate = useNavigate();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: {
        bg: 'bg-surface-3',
        text: 'text-text-secondary',
        label: t('restoreHistory.pending')
      },
      in_progress: {
        bg: 'bg-blue-500/12',
        text: 'text-blue-400',
        label: t('restoreHistory.inProgress')
      },
      awaiting_validation: {
        bg: 'bg-blue-500/12',
        text: 'text-blue-400',
        label: t('restoreHistory.awaitingValidation')
      },
      completed: {
        bg: 'bg-green-500/12',
        text: 'text-green-400',
        label: t('restoreHistory.completedStatus')
      },
      failed: {
        bg: 'bg-red-500/12',
        text: 'text-red-400',
        label: t('restoreHistory.failed')
      },
      cancelled: {
        bg: 'bg-surface-3',
        text: 'text-text-secondary',
        label: t('restoreHistory.cancelled')
      }
    };

    const badge = badges[status as keyof typeof badges] || badges.pending;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const canViewDetails = ['in_progress', 'awaiting_validation', 'failed'].includes(restore.status);
  const canDelete = ['completed', 'failed', 'cancelled'].includes(restore.status);

  return (
    <div className={`bg-surface-2 rounded-lg border border-border-1 p-4 hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            {getStatusBadge(restore.status)}
            <span className={`text-xs text-text-secondary`}>
              Cluster cible: <span className="font-mono font-medium">{restore.target_cluster}</span>
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className={`text-text-secondary`}>Démarrée:</span>
              <p className={`text-text-primary mt-0.5`}>{formatDate(restore.created_at)}</p>
            </div>

            {restore.completed_at && (
              <div>
                <span className={`text-text-secondary`}>Terminée:</span>
                <p className={`text-text-primary mt-0.5`}>{formatDate(restore.completed_at)}</p>
              </div>
            )}

            {restore.validated_at && restore.validated_by && (
              <div className="col-span-2">
                <span className={`text-text-secondary`}>Validée par:</span>
                <p className={`text-text-primary mt-0.5 text-xs`}>
                  {restore.validated_by} le {formatDate(restore.validated_at)}
                </p>
              </div>
            )}

            <div className="col-span-2">
              <span className={`text-text-secondary`}>ID:</span>
              <p className={`text-text-primary mt-0.5 font-mono text-xs truncate`}>
                {restore.id}
              </p>
            </div>
          </div>

          {restore.error_message && (
            <div className="mt-3 p-2 bg-red-500/12 rounded text-xs text-red-400">
              <span className="font-semibold">Erreur:</span> {restore.error_message}
            </div>
          )}
        </div>

        <div className="ml-4 flex flex-col gap-2">
          {canViewDetails && (
            <button
              onClick={() => navigate(`/deployments/${deploymentId}/restores/${restore.id}`)}
              className="px-3 py-1.5 rounded text-xs font-medium transition-colors bg-accent-600 hover:bg-accent-700 text-white"
              title="Voir les détails de la restauration"
            >
              Détails
            </button>
          )}
          {canDelete && onDelete && (
            <button
              onClick={() => onDelete(restore.id)}
              className="px-3 py-1.5 rounded text-xs font-medium transition-colors bg-red-600 hover:bg-red-700 text-white"
              title="Supprimer cette restauration"
            >
              Supprimer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestoreHistoryCard;
